import React from "react";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export type CategoryTreeNode = {
  id: string;
  name: string;
  parentId?: string | null;
  sortOrder?: number;
  children: CategoryTreeNode[];
};

export const UNSELECTED_CATEGORY_ID = "__unselected__";

function buildTree(categories: Array<{ id: string; name: string; parentId?: string | null; sortOrder?: number }>): CategoryTreeNode[] {
  const byId = new Map<string, CategoryTreeNode>();
  for (const c of categories) {
    byId.set(c.id, {
      id: c.id,
      name: c.name,
      parentId: c.parentId ?? null,
      sortOrder: c.sortOrder,
      children: [],
    });
  }

  const roots: CategoryTreeNode[] = [];
  for (const node of Array.from(byId.values())) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: CategoryTreeNode[]) => {
    nodes.sort((a, b) => {
      const ao = a.sortOrder ?? 0;
      const bo = b.sortOrder ?? 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });
    for (const n of nodes) sortNodes(n.children);
  };
  sortNodes(roots);

  return roots;
}

function collectDescendantIds(node: CategoryTreeNode): string[] {
  const out: string[] = [node.id];
  const walk = (n: CategoryTreeNode) => {
    for (const ch of n.children) {
      out.push(ch.id);
      walk(ch);
    }
  };
  walk(node);
  return out;
}

function filterTree(nodes: CategoryTreeNode[], query: string): CategoryTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;

  const walk = (n: CategoryTreeNode): CategoryTreeNode | null => {
    const selfMatch = n.name.toLowerCase().includes(q);
    const filteredChildren = n.children
      .map(walk)
      .filter((x): x is CategoryTreeNode => x !== null);
    if (selfMatch || filteredChildren.length > 0) {
      return { ...n, children: filteredChildren };
    }
    return null;
  };

  return nodes.map(walk).filter((x): x is CategoryTreeNode => x !== null);
}

function computeCheckState(node: CategoryTreeNode, selected: Set<string>): { checked: boolean; indeterminate: boolean } {
  const ids = collectDescendantIds(node);
  const selectedCount = ids.reduce((acc, id) => acc + (selected.has(id) ? 1 : 0), 0);
  if (selectedCount === 0) return { checked: false, indeterminate: false };
  if (selectedCount === ids.length) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
}

export default function CategoryFilterTree({
  categories,
  selectedCategoryIds,
  onChange,
}: {
  categories: Array<{ id: string; name: string; parentId?: string | null; sortOrder?: number }>;
  selectedCategoryIds: string[];
  onChange: (nextIds: string[]) => void;
}) {
  const tree = React.useMemo(() => buildTree(categories), [categories]);
  const selected = React.useMemo(() => new Set(selectedCategoryIds), [selectedCategoryIds]);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [query, setQuery] = React.useState("");

  const visibleTree = React.useMemo(() => filterTree(tree, query), [tree, query]);

  const toggleExpanded = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const toggleNode = (node: CategoryTreeNode) => {
    const ids = collectDescendantIds(node);
    const allSelected = ids.every((id) => selected.has(id));

    const next = new Set(selected);
    if (allSelected) {
      ids.forEach((id) => next.delete(id));
    } else {
      ids.forEach((id) => next.add(id));
    }
    onChange(Array.from(next));
  };

  const toggleSingle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const renderNode = (node: CategoryTreeNode, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = query.trim() ? true : (expanded[node.id] ?? depth < 1); // auto-expand while searching
    const state = computeCheckState(node, selected);

    return (
      <React.Fragment key={node.id}>
        <ListItem disablePadding sx={{ pl: depth * 1 }}>
          <ListItemButton
            dense
            onClick={() => toggleNode(node)}
            sx={{ borderRadius: 1, py: 0.25 }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Checkbox
                size="small"
                edge="start"
                checked={state.checked}
                indeterminate={state.indeterminate}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 600 : 400 }}>
                  {node.name}
                </Typography>
              }
            />
            {hasChildren && (
              <IconButton
                size="small"
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(node.id);
                }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {node.children.map((ch) => renderNode(ch, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box>
      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        size="small"
        placeholder="Search categories…"
        fullWidth
        sx={{ mb: 1 }}
      />

      <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto", pr: 0.5 }}>
        <ListItem disablePadding>
          <ListItemButton dense onClick={() => toggleSingle(UNSELECTED_CATEGORY_ID)} sx={{ borderRadius: 1, py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Checkbox size="small" checked={selected.has(UNSELECTED_CATEGORY_ID)} />
            </ListItemIcon>
            <ListItemText primary={<Typography variant="body2">Unselected</Typography>} />
          </ListItemButton>
        </ListItem>
        {visibleTree.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={<Typography variant="body2" color="text.secondary">No matches</Typography>}
            />
          </ListItem>
        ) : (
          visibleTree.map((n) => renderNode(n))
        )}
      </List>
    </Box>
  );
}
